/**
 * Luna API client.
 *
 * Single axios instance with the base URL pointing at /api/luna.
 * In dev, the Vite proxy forwards this to the backend on :5030.
 * In production, nginx routes the same path to the same backend.
 *
 * The Authorization header is injected from a JWT we obtain by sending
 * Telegram initData to the auth endpoint. For now we use a simple
 * in-memory token store; later we can persist into localStorage if
 * the user wants the session to survive page reloads.
 */
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

const API_BASE = '/api/luna';

let authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (authToken && config.headers) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error?: string; message?: string }>) => {
    // Surface the backend's structured error to the caller as the
    // axios error message — useful for toast notifications.
    if (error.response?.data?.message) {
      error.message = error.response.data.message;
    }
    return Promise.reject(error);
  },
);

// ----------------------------------------------------------------------------
// Endpoint helpers (typed wrappers around the raw API)
// ----------------------------------------------------------------------------

export interface MeResponse {
  success: boolean;
  user: {
    id: number;
    telegramId: number;
    username: string | null;
    isAdmin: boolean;
    usdtBalance: string;
    ofertaVersionAccepted: string | null;
    ofertaAcceptedAt: string | null;
  };
}

// ----------------------------------------------------------------------------
// Progress bars (NetworkTree: Personal / Network / L1-30d)
// ----------------------------------------------------------------------------

export interface ProgressBarData {
  current: string;
  required: string;
  remaining: string;
  progressPct: number;
}

export interface StatusProgress {
  currentLevel: number;
  currentName: string | null;
  nextLevel: number | null;
  nextName: string | null;
  nextBonusAmount: string | null;
  personal: ProgressBarData | null;
  network: ProgressBarData | null;
  isMaxed: boolean;
}

export interface RankProgress {
  currentLevel: number;
  currentName: string | null;
  nextLevel: number | null;
  nextName: string | null;
  nextBonusAmount: string | null;
  currentBoostPct: string | null;
  nextBoostPct: string | null;
  l1Turnover30d: ProgressBarData | null;
  isMaxed: boolean;
}

export interface LinesProgress {
  currentOpenLine: number;
  nextLine: number | null;
  activeCount: number;
  nextLineThreshold: number | null;
  remaining: number;
  progressPct: number;
  isMaxed: boolean;
}

export interface ProgressSnapshot {
  status: StatusProgress;
  rank: RankProgress;
  lines: LinesProgress;
  updatedAt: string;
}

export interface ProgressResponse {
  success: boolean;
  progress: ProgressSnapshot;
}

export const lunaApi = {
  health: () => api.get<{ success: boolean; status: string }>('/health'),
  me: () => api.get<MeResponse>('/auth/me'),

  progress: {
    snapshot: () => api.get<ProgressResponse>('/me/progress'),
    /** Relative URL путь для EventSource. Токен подставляется вызывающим. */
    streamUrl: (token: string) =>
      `/api/luna/me/progress/stream?token=${encodeURIComponent(token)}`,
  },

  /** Per-user стрим всего дашборда (баланс, контракты, реф-доход и т.д.). */
  user: {
    streamUrl: (token: string) =>
      `/api/luna/me/stream?token=${encodeURIComponent(token)}`,
  },

  slots: {
    config: () => api.get('/slots/config'),
    my: () => api.get('/slots/my'),
    purchase: (slotConfigId: number) =>
      api.post('/slots/purchase', { slotConfigId }),
  },

  claim: {
    pending: () => api.get('/claim/pending'),
    /**
     * Claim с авто-retry на сетевых ошибках.
     *
     * Retry ТОЛЬКО при:
     *   - нет ответа (network timeout, ECONNABORTED)
     *   - 5xx от сервера (502/503/504 — переходный glitch)
     *
     * НЕ retry при:
     *   - 4xx (400 CLAIM_TOO_EARLY, 401, 429 RATE_LIMIT, 403 BLOCKED)
     *     — это бизнес-ошибки, повторять бесполезно
     *
     * Backoff: 500ms → 1s → 2s (суммарно до ~3.5с ожидания).
     * Идемпотентность: бэкенд использует FOR UPDATE + last_global_claim_at,
     * двойной запрос с тем же юзером → второй получит CLAIM_TOO_EARLY,
     * что безопасно.
     */
    execute: async () => {
      const delays = [500, 1000, 2000];
      let lastErr: unknown = null;
      for (let attempt = 0; attempt <= delays.length; attempt++) {
        try {
          return await api.post('/claim');
        } catch (err: unknown) {
          lastErr = err;
          const e = err as {
            response?: { status?: number };
            code?: string;
            message?: string;
          };
          const status = e.response?.status;
          const isNetworkErr =
            !status ||
            e.code === 'ECONNABORTED' ||
            e.code === 'ERR_NETWORK' ||
            /network|timeout/i.test(e.message ?? '');
          const is5xx = status !== undefined && status >= 500 && status < 600;
          const shouldRetry = isNetworkErr || is5xx;
          if (!shouldRetry || attempt === delays.length) throw err;
          await new Promise((r) => setTimeout(r, delays[attempt]));
        }
      }
      throw lastErr;
    },
  },

  pool: {
    status: () => api.get('/pool/status'),
  },

  shareCard: {
    /** Генерирует stories-плашку для последнего клейма.
     *  Возвращает публичный URL картинки (24h TTL). */
    generate: (amount: number) =>
      api.post<{
        success: boolean;
        ok: boolean;
        url: string;
        filename: string;
        share_url: string;
      }>('/share-card', { amount }),
  },

  quickStart: {
    status: () => api.get('/quick-start/status'),
    claimBonus: () =>
      api.post<{
        success: true;
        bonusContractId: string;
        amountUsdt: string;
        windowNumber: number;
      }>('/quick-start/claim-bonus'),
  },

  plasmaCore: {
    status: (contractId: string) => api.get(`/plasma-core/${contractId}/status`),
    activate: (contractId: string) => api.post(`/plasma-core/${contractId}/activate`),
    recharge: (contractId: string) => api.post(`/plasma-core/${contractId}/recharge`),
    /** Перезарядить ВСЕ PC юзера одной кнопкой ($1 × N) */
    rechargeAll: () => api.post(`/plasma-core/recharge-all`),
  },

  referrals: {
    tree: () => api.get('/referrals/tree'),
    link: () => api.get('/referrals/link'),
    graph: () => api.get('/referrals/graph'),
    teamSummary: () => api.get('/referrals/team/summary'),
    teamPartners: (params: {
      limit?: number;
      offset?: number;
      sortBy?: 'turnover' | 'slots' | 'income';
      activeOnly?: boolean;
      line?: number | null;
    } = {}) => {
      const q = new URLSearchParams();
      q.set('limit', String(params.limit ?? 50));
      q.set('offset', String(params.offset ?? 0));
      if (params.sortBy) q.set('sortBy', params.sortBy);
      if (params.activeOnly) q.set('activeOnly', 'true');
      if (params.line != null) q.set('line', String(params.line));
      return api.get(`/referrals/team/partners?${q.toString()}`);
    },
  },

  p2p: {
    /** target — @username, username или числовой TG ID */
    send: (target: string, amountUsdt: string) =>
      api.post('/p2p/send', { target, amountUsdt }),
  },

  withdraw: {
    limit: () => api.get('/withdraw/limit'),
    /**
     * Создаёт запрос на вывод. Ответ содержит id + verificationExpiresAt.
     * После этого юзер должен ввести 6-значный код через verify().
     */
    request: (
      amountUsdt: string,
      destinationAddress: string,
      network: 'BEP20' | 'TON',
      verificationChannel: 'telegram' | 'email' = 'telegram',
    ) => api.post<{
      success: boolean;
      id: string;
      amountRequested: string;
      commission: string;
      amountNet: string;
      status: 'awaiting_verification';
      verificationChannel: 'telegram' | 'email';
      verificationExpiresAt: string;
    }>('/withdraw', { amountUsdt, destinationAddress, network, verificationChannel }),
    verify: (withdrawalId: number | string, code: string) =>
      api.post<{
        success: boolean;
        status: 'pending';
        verifiedAt: string;
        message: string;
      }>(`/withdraw/${withdrawalId}/verify`, { code }),
    resend: (withdrawalId: number | string, channel: 'telegram' | 'email' = 'telegram') =>
      api.post<{
        success: boolean;
        verificationChannel: 'telegram' | 'email';
        verificationExpiresAt: string;
        message: string;
      }>(`/withdraw/${withdrawalId}/resend`, { channel }),
  },

  deposit: {
    /**
     * В test-режиме: возвращает { mode: 'test', credited, newBalance }.
     * В production: { mode: 'production', wallet: { id, address, network } }.
     */
    wallet: (network: 'BSC' | 'TON') =>
      api.post<
        | { success: true; mode: 'test'; credited: string; newBalance: string; message: string }
        | {
            success: true;
            mode: 'production';
            wallet: { id: number; network: 'BSC' | 'TON'; address: string; createdAt: string };
          }
      >('/deposit/wallet', { network }),
    /**
     * Read-only: просто отдаёт существующий кошелёк без создания / кредита.
     * Используется DepositCard на mount чтобы сразу показать готовый адрес.
     * Если кошелька ещё нет — { wallet: null }.
     */
    walletStatus: (network: 'BSC' | 'TON') =>
      api.get<{
        success: true;
        mode: 'test' | 'production';
        wallet: {
          id: number;
          network: 'BSC' | 'TON';
          address: string;
          createdAt: string;
        } | null;
      }>(`/deposit/wallet?network=${network}`),
    history: (network?: 'BSC' | 'TON', limit = 20) => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (network) params.set('network', network);
      return api.get<{
        success: boolean;
        deposits: Array<{
          id: number;
          amount: string;
          network: 'BSC' | 'TON';
          status: 'detected' | 'confirming' | 'credited' | 'failed';
          confirmations: number;
          tx_hash: string;
          sender_address: string | null;
          credited_at: string | null;
          created_at: string;
        }>;
      }>(`/deposit/history?${params.toString()}`);
    },
    /** Ручной rescan — кнопка "Я оплатил" */
    check: (network: 'BSC' | 'TON') =>
      api.post<{ success: boolean; processed: number; message: string }>(
        '/deposit/check',
        { network },
      ),
  },

  walletHistory: {
    list: (limit = 20, offset = 0, hide?: string[]) => {
      const hideQ = hide && hide.length > 0
        ? `&hide=${encodeURIComponent(hide.join(','))}`
        : '';
      return api.get(`/me/wallet-history?limit=${limit}&offset=${offset}${hideQ}`);
    },
  },

  notifications: {
    list: (limit = 50) => api.get(`/notifications?limit=${limit}`),
    markRead: () => api.post('/notifications/read'),
    clearAll: () => api.delete('/notifications'),
  },

  onboarding: {
    status: (config?: { signal?: AbortSignal }) =>
      api.get('/onboarding/status', config),
    start: () => api.post('/onboarding/start'),
    step: (toStep: number) => api.post('/onboarding/step', { toStep }),
    strategy: (strategy: 'passive' | 'growth' | 'network') =>
      api.post('/onboarding/strategy', { strategy }),
    sandboxEvent: (stage: 'started' | 'finished', details?: Record<string, unknown>) =>
      api.post('/onboarding/sandbox-event', { stage, details }),
    complete: () => api.post('/onboarding/complete'),
    skip: () => api.post('/onboarding/skip'),
    updateChecklist: (
      key: 'activated' | 'first_claim' | 'claim_collected' | 'invited_partner' | 'plasma_core',
      value: boolean,
    ) => api.post(`/onboarding/checklist/${key}`, { value }),
    reset: () => api.post('/onboarding/reset'),
  },

  oferta: {
    status: () =>
      api.get<{
        success: boolean;
        currentVersion: string;
        acceptedVersion: string | null;
        acceptedAt: string | null;
        isCurrent: boolean;
        shouldShow: boolean;
      }>('/oferta/status'),
    content: () =>
      api.get<{ success: boolean; version: string; content: string }>(
        '/oferta/content',
      ),
    accept: () =>
      api.post<{ success: boolean; version: string; acceptedAt: string }>(
        '/oferta/accept',
      ),
  },

  admin: {
    resetUserOnboarding: (userId: number, force = false) =>
      api.post(`/admin/users/${userId}/onboarding/reset`, { force }),
    resetUserOferta: (userId: number) =>
      api.post(`/admin/users/${userId}/oferta/reset`),
  },
};
